import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, UploadCloud, X, CheckCircle2, RefreshCw } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

const DISTRICTS = ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Hazaribagh", "Deoghar", "Giridih", "Palamu", "Chaibasa", "Dumka"];
const MAX_MB = 3;

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

/** Compress an image data URL to keep base64 payloads small. */
async function compressDataUrl(dataUrl, maxSide = 900, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

function PhotoCapture({ value, onChange, testIdPrefix }) {
  const [mode, setMode] = useState("upload"); // upload | camera
  const [streaming, setStreaming] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setStreaming(false);
  };

  useEffect(() => () => stopStream(), []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStreaming(true);
    } catch (e) {
      toast.error("Camera access denied. Please use the upload option instead.");
      setMode("upload");
    }
  };

  const snap = async () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth || 640;
    canvas.height = v.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    const raw = canvas.toDataURL("image/jpeg", 0.9);
    const compressed = await compressDataUrl(raw);
    onChange(compressed);
    stopStream();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Photo must be under ${MAX_MB}MB`); return; }
    const raw = await fileToDataUrl(f);
    const compressed = await compressDataUrl(raw);
    onChange(compressed);
  };

  if (value) {
    return (
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 rounded-sm overflow-hidden border border-jpa-border bg-jpa-surface">
          <img src={value} alt="profile" className="w-full h-full object-cover" />
        </div>
        <button type="button" data-testid={`${testIdPrefix}-clear`} onClick={() => onChange(null)}
          className="text-xs font-bold uppercase tracking-widest text-jpa-live hover:text-red-700">
          <X className="w-4 h-4 inline mr-1" /> Remove & retake
        </button>
      </div>
    );
  }

  return (
    <div className="border border-jpa-border rounded-sm p-3">
      <div className="flex gap-2 mb-3">
        <button type="button" data-testid={`${testIdPrefix}-mode-upload`}
          onClick={() => { stopStream(); setMode("upload"); }}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-sm ${mode === "upload" ? "bg-jpa-primary text-white" : "bg-white text-jpa-ink border border-jpa-border"}`}>
          <UploadCloud className="w-3 h-3 inline mr-1" /> Upload
        </button>
        <button type="button" data-testid={`${testIdPrefix}-mode-camera`}
          onClick={() => { setMode("camera"); startCamera(); }}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest rounded-sm ${mode === "camera" ? "bg-jpa-primary text-white" : "bg-white text-jpa-ink border border-jpa-border"}`}>
          <Camera className="w-3 h-3 inline mr-1" /> Use camera
        </button>
      </div>
      {mode === "upload" ? (
        <label className="flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-4 cursor-pointer hover:border-jpa-primary transition-colors">
          <UploadCloud className="w-5 h-5 text-jpa-primary" />
          <span className="text-sm text-jpa-muted">Click to upload a profile photo (up to {MAX_MB}MB)</span>
          <input type="file" accept="image/*" className="hidden" data-testid={`${testIdPrefix}-file`} onChange={onFile} />
        </label>
      ) : (
        <div>
          <video ref={videoRef} className="w-full max-w-xs rounded-sm bg-black" playsInline muted />
          <div className="mt-2 flex gap-2">
            <Button type="button" data-testid={`${testIdPrefix}-snap`} onClick={snap} disabled={!streaming}
              className="bg-jpa-primary hover:bg-jpa-primary-hover text-white rounded-sm text-xs uppercase tracking-widest font-bold">
              <Camera className="w-4 h-4 mr-1" /> Capture
            </Button>
            <Button type="button" variant="outline" onClick={startCamera} className="rounded-sm text-xs">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AadhaarUpload({ label, value, onChange, testId }) {
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${label} must be under ${MAX_MB}MB`); return; }
    const raw = await fileToDataUrl(f);
    const compressed = await compressDataUrl(raw, 1200, 0.85);
    onChange(compressed);
  };
  return (
    <div>
      <Label>{label}</Label>
      {value ? (
        <div className="mt-1 relative w-full max-w-[220px] border border-jpa-border rounded-sm overflow-hidden">
          <img src={value} alt={label} className="w-full h-auto" />
          <button type="button" data-testid={`${testId}-clear`} onClick={() => onChange(null)}
            className="absolute top-1 right-1 bg-jpa-live text-white rounded-full p-1">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <label className="mt-1 flex items-center gap-3 border-2 border-dashed border-jpa-border rounded-sm p-3 cursor-pointer hover:border-jpa-primary transition-colors">
          <UploadCloud className="w-4 h-4 text-jpa-primary" />
          <span className="text-xs text-jpa-muted">Click to upload {label}</span>
          <input type="file" accept="image/*" className="hidden" data-testid={testId} onChange={onFile} />
        </label>
      )}
    </div>
  );
}

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", district: "Ranchi",
    gender: "M", dob: "", dupr_id: "", pgid: "",
  });
  const [photo, setPhoto] = useState(null);
  const [aadhaarFront, setAadhaarFront] = useState(null);
  const [aadhaarBack, setAadhaarBack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.dob) { toast.error("Date of birth is required for age category verification."); return; }
    if (!photo) { toast.error("Please upload or capture a profile photo."); return; }
    if (!aadhaarFront || !aadhaarBack) { toast.error("Please upload both Aadhaar card front and back."); return; }
    setLoading(true);
    const payload = {
      ...form,
      photo_data_url: photo,
      aadhaar_front_data_url: aadhaarFront,
      aadhaar_back_data_url: aadhaarBack,
    };
    if (!payload.dupr_id) delete payload.dupr_id;
    if (!payload.pgid) delete payload.pgid;
    const res = await register(payload);
    setLoading(false);
    if (res.ok && res.pending) {
      setPending(true);
    } else if (res.ok) {
      toast.success("Welcome to JPA!");
      nav("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  if (pending) {
    return (
      <div data-testid="register-pending-success" className="max-w-2xl mx-auto px-4 md:px-8 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-jpa-primary mx-auto mb-6" />
        <h1 className="font-display text-4xl tracking-wider mb-3">REGISTRATION SUBMITTED</h1>
        <p className="text-jpa-muted mb-8">
          Thanks for registering with JPA, <b>{form.name}</b>! Your player registration is now under review.
          JPA will manually verify your Aadhaar and details, then issue your unique JPA ID.
          You&apos;ll be notified via email once approved.
        </p>
        <Button data-testid="pending-home-btn" onClick={() => nav("/")} className="bg-jpa-primary hover:bg-jpa-primary-hover text-white uppercase tracking-widest font-bold rounded-sm">
          Back to Home
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="register-page" className="min-h-[calc(100vh-80px)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-jpa-ink">
        <img src="https://images.unsplash.com/photo-1617144520113-88ae706a86eb?w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-jpa-primary/80 via-jpa-ink/60 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <span className="bg-jpa-accent text-jpa-ink px-3 py-1 text-xs font-bold uppercase tracking-widest skew-badge w-fit">Join JPA</span>
          <h1 className="font-display text-7xl tracking-wider mt-4">CLAIM<br />YOUR<br />RANKING.</h1>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-12 overflow-y-auto">
        <form onSubmit={submit} className="w-full max-w-md py-4">
          <div className="text-xs font-bold uppercase tracking-widest text-jpa-primary">Player registration</div>
          <h2 className="font-display text-4xl tracking-wider mt-1">Create profile</h2>

          <div className="mt-6"><Label>Full name *</Label><Input required data-testid="register-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div className="mt-3"><Label>Email *</Label><Input required type="email" data-testid="register-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div className="mt-3"><Label>Password *</Label><PasswordInput required data-testid="register-password" toggleTestId="register-password-toggle" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-sm" /></div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <Label>Gender *</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger data-testid="register-gender" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="M">Male</SelectItem>
                  <SelectItem value="F">Female</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Date of Birth *</Label>
              <Input required type="date" data-testid="register-dob" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="mt-1 rounded-sm" />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><Label>Phone * (10 digits)</Label><Input required inputMode="numeric" maxLength={10} pattern="\d{10}" data-testid="register-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="9876543210" className="mt-1 rounded-sm" /></div>
            <div>
              <Label>District *</Label>
              <Select value={form.district} onValueChange={(v) => setForm({ ...form, district: v })}>
                <SelectTrigger data-testid="register-district" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{DISTRICTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <div><Label>DUPR ID <span className="text-xs text-jpa-muted">(optional)</span></Label><Input data-testid="register-dupr" value={form.dupr_id} onChange={(e) => setForm({ ...form, dupr_id: e.target.value })} placeholder="e.g. 5A2B7X" className="mt-1 rounded-sm" /></div>
            <div><Label>PGID <span className="text-xs text-jpa-muted">(optional)</span></Label><Input data-testid="register-pgid" value={form.pgid} onChange={(e) => setForm({ ...form, pgid: e.target.value })} placeholder="PGID" className="mt-1 rounded-sm" /></div>
          </div>

          <div className="mt-5">
            <Label>Profile photo *</Label>
            <div className="mt-1"><PhotoCapture value={photo} onChange={setPhoto} testIdPrefix="register-photo" /></div>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <AadhaarUpload label="Aadhaar front *" value={aadhaarFront} onChange={setAadhaarFront} testId="register-aadhaar-front" />
            <AadhaarUpload label="Aadhaar back *" value={aadhaarBack} onChange={setAadhaarBack} testId="register-aadhaar-back" />
          </div>

          <Button disabled={loading} type="submit" data-testid="register-submit" className="w-full mt-6 bg-jpa-primary hover:bg-jpa-primary-hover text-white font-bold uppercase tracking-widest rounded-sm h-12">
            {loading ? "Submitting..." : "Submit for JPA verification"}
          </Button>
        </form>
      </div>
    </div>
  );
}
